import React, { useEffect, useState } from "react"
import { Box, styled, useTheme, useMediaQuery } from "@mui/material"
import { ReactComponent as AttentionIcon } from "src/components/browser/icons/icon_attention.svg"
import { textColor } from "src/components/ColoredDialog"
import { useUsageLimit } from "src/providers/UsageLimitProvider"
import { useNavigate } from "react-router-dom"
import { TwoSidedModal } from "src/sites/home/TwoSidedModal"

interface UsageLimitDialogProps {
  pageName: string // Unique identifier for the page (e.g., tablet.name, tablet.id, or 'text')
}

type LimitLevel = "halfway" | "almost" | "reached"

// Reusing colors from ColoredDialog system for text/icons
const WARNING_TEXT_COLOR = textColor("warning") // "#EE9C3C"

// Usage limit dialog specific colors
const USAGE_WARNING_BG_COLOR = "#f9e6b1" // Yellow/gold background for warning state (left side)
const USAGE_REACHED_BG_COLOR = "#FFE5E5" // Light red background for reached state (left side)
const USAGE_REACHED_COLOR = "#ec5351" // Red for limit reached
const RIGHT_SIDE_BG_COLOR = "#fffbf1" // Cream background for content area

const getLimitLevel = (used: number, total: number | null): LimitLevel | undefined => {
  // If total is null (unlimited), dialog should not show
  if (total === null) return undefined
  if (used >= total) return "reached"
  if (used >= total * 0.8) return "almost"
  if (used >= total * 0.5) return "halfway"
  return undefined
}

const getBackgroundColor = (level: LimitLevel): string => {
  switch (level) {
    case "halfway":
      return USAGE_WARNING_BG_COLOR
    case "almost":
      return USAGE_WARNING_BG_COLOR
    case "reached":
      return USAGE_REACHED_BG_COLOR
  }
}

const getTitleColor = (level: LimitLevel): string => {
  return level === "reached" ? USAGE_REACHED_COLOR : WARNING_TEXT_COLOR
}

const getIconColor = (level: LimitLevel): string => {
  return level === "reached" ? USAGE_REACHED_COLOR : WARNING_TEXT_COLOR
}

const getTitle = (level: LimitLevel): string => {
  switch (level) {
    case "halfway":
      return "Halfway there"
    case "almost":
      return "Almost at your limit"
    case "reached":
      return "Monthly limit reached"
  }
}

const formatCharacters = (count: number, useKFormat: boolean): string => {
  if (useKFormat) {
    return `${Math.floor(count / 1000)} K`
  }
  return `${count}`
}

const getContentText = (level: LimitLevel, used: number, total: number | null): React.ReactNode => {
  // If total is null (unlimited), don't show content
  if (total === null) return null

  const useKFormat = total > 10000
  const usedFormatted = formatCharacters(used, useKFormat)
  const totalFormatted = formatCharacters(total, useKFormat)

  switch (level) {
    case "halfway":
      return (
        <>
          You&apos;ve used <b>{usedFormatted} of your {totalFormatted} monthly characters</b> for translations and edits.
        </>
      )
    case "almost":
      return (
        <>
          You&apos;ve used <b>{usedFormatted} of your {totalFormatted} monthly characters</b>.
        </>
      )
    case "reached":
      return (
        <>
          You&apos;ve used <b>your {totalFormatted} characters</b> for this month.
        </>
      )
  }
}

const IconCircle = styled(Box)<{ limitlevel: LimitLevel; ismobile?: boolean }>(({ limitlevel, ismobile }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  marginBottom: ismobile ? "-30px" : "0",
  "& svg": {
    width: ismobile ? "70px" : "140px",
    height: ismobile ? "70px" : "140px",
    color: getIconColor(limitlevel),
    fill: getIconColor(limitlevel)
  }
}))

const UsageLimitDialog: React.FC<UsageLimitDialogProps> = ({ pageName }) => {
  const [open, setOpen] = useState(false)
  const { usedCharacters, userLimit, checkUsageLimits } = useUsageLimit()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  useEffect(() => {
    const checkAndShowDialog = async () => {
      await checkUsageLimits()
    }
    checkAndShowDialog()
  }, [pageName])

  useEffect(() => {
    // Show dialog if usage is >= 50% whenever usage data or page changes
    // If userLimit is null (unlimited), never show dialog
    if (userLimit !== null && usedCharacters >= userLimit * 0.5) {
      setOpen(true)
    }
  }, [usedCharacters, userLimit, pageName])

  const handleClose = () => {
    setOpen(false)
  }

  const limitLevel = getLimitLevel(usedCharacters, userLimit)

  // Don't render dialog if limitLevel is undefined (unlimited or below 50%)
  if (!limitLevel) {
    return null
  }

  const icon = (
    <IconCircle limitlevel={limitLevel} ismobile={isMobile}>
      <AttentionIcon />
    </IconCircle>
  )

  const contentText = getContentText(limitLevel, usedCharacters, userLimit)

  return (
    <TwoSidedModal
      open={open}
      onClose={handleClose}
      icon={icon}
      leftBackgroundColor={getBackgroundColor(limitLevel)}
      rightBackgroundColor={RIGHT_SIDE_BG_COLOR}
      title={getTitle(limitLevel)}
      titleColor={getTitleColor(limitLevel)}
      titleStyle={{
        font: "normal normal 600 32px/40px Inter"
      }}
      content={contentText}
      contentStyle={{
        color: "#333333",
        fontSize: "16px",
        lineHeight: "26px"
      }}
      buttonStyle={{
        backgroundColor: "#003B5C",
        color: "#fff",
        padding: "14px 56px",
        fontSize: "16px",
        fontWeight: 500,
        textTransform: "none",
        borderRadius: "30px",
        "&:hover": {
          backgroundColor: "#002940"
        }
      }}
      color="primary"
      maxWidth="800px"
    />
  )
}

export default UsageLimitDialog

